import React, { useEffect, useMemo, useRef, useState } from "react";
import { IconButton } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import * as style from "./ApplicationAccess.module.scss";
import { styled } from "@mui/material/styles";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import { Checkbox, Divider } from "cprime-foundational-components";
import Typography from "../../Components/Common/CprimeText";

interface Feature {
  id: number;
  featureName: string;
  featureDescription: string | null;
  disabled?: boolean;
  isChecked?: boolean;
}

interface FeatureGroup {
  groupId: number;
  groupName: string;
  groupDescription: string;
  features: Feature[];
}

const CustomTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.black,
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
    fontSize: theme.typography.pxToRem(12),
    maxWidth: 300,
    border: "1px solid #dadde9",
    padding: theme.spacing(1),
    whiteSpace: "pre-line",
  },
}));

const getFeatureDescriptionTooltip = (
  featureDescription: string | null,
): React.ReactNode => {
  if (!featureDescription || featureDescription.trim() === "") {
    return "No description available";
  }

  return featureDescription
    .replace(/&lt;br\s*\/?&gt;/gi, "\n")
    .replace(/<br\s*\/?\s*>/gi, "\n")
    .replace(/\\n/g, "\n");
};

const Card: React.FC<{
  group: FeatureGroup;
  expandedCards: any;
  toggleAllFeatures: any;
  toggleFeature: any;
}> = ({ group, expandedCards, toggleAllFeatures, toggleFeature }) => {
  const scssClass = style as any;
  const ref = useRef<HTMLDivElement | null>(null);
  const [isOverflow, setIsOverflow] = useState(false);
  const zoomSize = Math?.round(window?.devicePixelRatio * 100);

  useEffect(() => {
    if (ref?.current) {
      setIsOverflow(ref?.current?.scrollWidth > ref?.current?.offsetWidth);
    }
  }, []);

  const allChecked = useMemo(() => {
    const enabledFeatures = group?.features?.filter(
      (feature) => feature.disabled === false,
    );
    if (enabledFeatures?.length > 0) {
      return enabledFeatures.every((feature) => feature.isChecked === true);
    }
    return false;
  }, [group]);

  const selectedFeaturesLength = group?.features?.filter(
    (feature) => feature.isChecked,
  )?.length;

  return (
    <div className={scssClass.container}>
      <div className={scssClass.header}>
        <div className={scssClass.itemGroup}>
          <CustomTooltip
            title={
              isOverflow || (selectedFeaturesLength > 0 && zoomSize > 80)
                ? group.groupName
                : ""
            }
          >
            <Typography
              ref={ref}
              className={scssClass.groupName}
              noWrap
              sx={{
                fontSize: "20px !important",
                lineHeight: "28px !important",
                fontWeight: "600 !important",
                color: "#000000 !important",
              }}
            >
              {group.groupName}
            </Typography>
          </CustomTooltip>
        </div>
        {selectedFeaturesLength > 0 && (
          <div
            style={{
              display: `${selectedFeaturesLength > 0 ? "block" : "none"}`,
              marginLeft: "40px",
            }}
          >
            <span className={scssClass.selectedCount}>
              {`${selectedFeaturesLength} Selected`}
            </span>
          </div>
        )}
      </div>

      {/* Group description */}
      <Typography
        className={scssClass.groupDescription}
        style={{
          height: "3.2rem",
          fontSize: "14px",
          paddingBottom: "0px",
          paddingTop: "5px",
        }}
      >
        {group.groupDescription}
      </Typography>

      {/* Features section */}
      {expandedCards[group.groupId] && (
        <div className={scssClass.featureSection}>
          {/* Select all checkbox */}
          {group?.features?.length > 1 && (
            <>
              <Checkbox
                label={
                  <span
                    style={{
                      fontSize: "14px",
                      lineHeight: "16px",
                      fontWeight: "400",
                      color: "#000",
                      opacity: group?.features?.every(
                        (f) => f.disabled === true,
                      )
                        ? 0.5
                        : 1,
                    }}
                  >
                    All
                  </span>
                }
                disabled={group?.features?.every((f) => f.disabled === true)}
                checked={allChecked}
                onChange={(e) =>
                  toggleAllFeatures(group.groupId, e.target.checked)
                }
              />
              <Divider
                variant={"fullWidth"}
                sx={{ borderColor: "#d9d9d9", margin: "12px 0px" }}
              />
            </>
          )}
          <div
            className="ml-4 space-y-1"
            style={{ display: "flex", flexDirection: "column" }}
          >
            {group.features.map((feature, index) => (
              <div className={scssClass.featureItem} key={index}>
                <div
                  className={scssClass.featureRow}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    opacity: feature?.disabled ? 0.5 : 1,
                  }}
                >
                  <Checkbox
                    label={
                      <div className={scssClass.gridrow}>
                        <div className={scssClass.gridcol}>
                          <span
                            style={{
                              fontSize: "14px",
                              lineHeight: "18px",
                              fontWeight: "400",
                              color: "#000",
                            }}
                          >
                            {feature.featureName}
                          </span>
                        </div>
                        {/* Info icon with popover */}
                      </div>
                    }
                    checked={feature?.isChecked}
                    disabled={feature?.disabled}
                    onChange={() => toggleFeature(group.groupId, feature.id)}
                  />
                  <div className={scssClass.gridcol2}>
                    <CustomTooltip
                      title={getFeatureDescriptionTooltip(
                        feature.featureDescription,
                      )}
                    >
                      <IconButton
                        size="medium"
                        sx={{
                          "&.MuiSvgIcon-root": {
                            fill: "#000",
                            fontSize: "16px",
                          },
                        }}
                      >
                        <InfoOutlinedIcon
                          fontSize="medium"
                          sx={{
                            "&.MuiSvgIcon-root": {
                              fill: "#000",
                              fontSize: "16px",
                            },
                          }}
                        />
                      </IconButton>
                    </CustomTooltip>
                  </div>
                </div>
                {group?.features?.length - 1 !==
                  group?.features?.indexOf(feature) && (
                  <Divider
                    variant={"fullWidth"}
                    sx={{ borderColor: "#d9d9d9", margin: "12px 0px" }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Card;
